import React, { useState } from 'react'
import { Settings, Save, AlertCircle } from 'lucide-react'
import toast from 'react-hot-toast'

const SystemSettings = () => {
  const [settings, setSettings] = useState({
    maxDailyWithdrawal: 10000,
    maxTransferAmount: 50000,
    minTransferAmount: 1,
    requireDepositApproval: true,
    requireWithdrawalApproval: true,
    requireTransferApproval: false,
    maintenanceMode: false
  })

  const [loading, setLoading] = useState(false)

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setSettings({
      ...settings,
      [name]: type === 'checkbox' ? checked : value
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setLoading(true)

    setTimeout(() => {
      toast.success('Settings updated successfully!')
      setLoading(false)
    }, 1000)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
          System Settings
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Configure system-wide parameters and limits
        </p>
      </div>

      <div className="alert alert-warning">
        <AlertCircle className="w-5 h-5" />
        <div>
          <p className="font-medium">Caution: System-wide Changes</p>
          <p className="text-sm">Changes here will affect all users and transactions. Proceed carefully.</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Transaction Limits */}
        <div className="card">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
            Transaction Limits
          </h2>
          <div className="space-y-4">
            <div className="input-group">
              <label htmlFor="maxDailyWithdrawal" className="input-label">
                Maximum Daily Withdrawal ($)
              </label>
              <input
                id="maxDailyWithdrawal"
                name="maxDailyWithdrawal"
                type="number"
                value={settings.maxDailyWithdrawal}
                onChange={handleChange}
                min="0"
              />
            </div>

            <div className="input-group">
              <label htmlFor="maxTransferAmount" className="input-label">
                Maximum Transfer Amount ($)
              </label>
              <input
                id="maxTransferAmount"
                name="maxTransferAmount"
                type="number"
                value={settings.maxTransferAmount}
                onChange={handleChange}
                min="0"
              />
            </div>

            <div className="input-group">
              <label htmlFor="minTransferAmount" className="input-label">
                Minimum Transfer Amount ($)
              </label>
              <input
                id="minTransferAmount"
                name="minTransferAmount"
                type="number"
                value={settings.minTransferAmount}
                onChange={handleChange}
                min="0"
              />
            </div>
          </div>
        </div>

        {/* Approval Settings */}
        <div className="card">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
            Approval Requirements
          </h2>
          <div className="space-y-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="requireDepositApproval"
                checked={settings.requireDepositApproval}
                onChange={handleChange}
                className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  Require Deposit Approval
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  All deposit requests must be approved by admin
                </p>
              </div>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="requireWithdrawalApproval"
                checked={settings.requireWithdrawalApproval}
                onChange={handleChange}
                className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  Require Withdrawal Approval
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  All withdrawal requests must be approved by admin
                </p>
              </div>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="requireTransferApproval"
                checked={settings.requireTransferApproval}
                onChange={handleChange}
                className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  Require Transfer Approval
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  All transfer requests must be approved by admin
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* System Status */}
        <div className="card">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
            System Status
          </h2>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              name="maintenanceMode"
              checked={settings.maintenanceMode}
              onChange={handleChange}
              className="w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-500"
            />
            <div>
              <p className="font-medium text-gray-900 dark:text-white">
                Maintenance Mode
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Temporarily disable user transactions for system maintenance
              </p>
            </div>
          </label>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary"
          >
            {loading ? (
              <>
                <div className="spinner w-5 h-5" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Save Settings
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default SystemSettings